import React, { useRef, useState, useEffect } from 'react';
import { getSurveys, saveTemplate, getTemplate, getDynamicQuestions, getCategoryForAge, getCategoryName, getMemberAge, getMemberName, getMemberPhoto, getMemberPhone, saveTemplateByType, getTemplateByType } from '../services/storage';
import { CardField, SurveyData, DynamicQuestion } from '../types';
import { drawCardOnCanvas, downloadCanvas, AVAILABLE_FIELDS, formatSerialNoForDisplay } from '../utils/cardUtils';
import { Upload, Sliders, Plus, Trash2, MousePointer2, Loader, Save, Check, AlertCircle, Camera } from 'lucide-react';

const AVAILABLE_FONTS = [
  { value: 'Arial', label: 'Arial (Sans-serif)' },
  { value: 'Manjari', label: 'Manjari (Malayalam Rounded)' },
  { value: 'Gayathri', label: 'Gayathri (Malayalam Bold)' },
  { value: 'Chilanka', label: 'Chilanka (Malayalam Handwriting)' },
  { value: 'Meera', label: 'Meera (Malayalam Serif)' },
  { value: 'Inter', label: 'Inter (Clean English)' },
  { value: 'Space Grotesk', label: 'Space Grotesk (Modern Tech)' },
  { value: 'Playfair Display', label: 'Playfair Display (Elegant Serif)' },
  { value: 'Courier New', label: 'Courier New (Monospace)' },
  { value: 'Times New Roman', label: 'Times New Roman (Classic Serif)' },
];

// Helper to compress image to safe Base64 size for Firestore (max 1MB doc size)
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // Reduce max width to 800px to ensure size stays well under 1MB
        const MAX_WIDTH = 800; 
        let width = img.width;
        let height = img.height;
        
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        if(ctx) {
             ctx.drawImage(img, 0, 0, width, height);
             // Use 0.6 quality for aggressive compression while maintaining readability
             const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
             resolve(dataUrl);
        } else {
             reject(new Error("Canvas context failed"));
        }
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
};

const CardGenerator: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentType, setCurrentType] = useState<'student' | 'faculty' | 'coordinator'>('student');
  const [templateImg, setTemplateImg] = useState<string | null>(null);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [fields, setFields] = useState<CardField[]>([]);
  const [photoConfig, setPhotoConfig] = useState<CardTemplate['photoConfig']>(undefined);
  const [selectedSurveyIndex, setSelectedSurveyIndex] = useState(0);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [availableFields, setAvailableFields] = useState<{ key: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    fieldIndex: number | null;
    startX: number;
    startY: number;
    originalX: number;
    originalY: number;
  } | null>(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [savedSurveys, fetchedQuestions] = await Promise.all([
          getSurveys({ limitCount: 20 }),
          getDynamicQuestions()
        ]);
        setSurveys(savedSurveys);
        setQuestions(fetchedQuestions);
      } catch (e) {
        console.error(e);
      }
    };
    fetchMetadata();
  }, []);

  useEffect(() => {
    if (currentType === 'student') {
        const mapped = [
          { key: 'serialNo', label: 'ക്രമനമ്പർ / ID No' },
          { key: 'categoryName', label: 'വിഭാഗം (Category Name)' },
          ...questions.map(q => ({ key: q.id, label: q.label }))
        ];
        if (!mapped.some(m => m.key === 'barcode')) {
          mapped.push({ key: 'barcode', label: 'ബാർകോഡ് (Barcode)' });
        }
        if (!mapped.some(m => m.key === 'qrCode')) {
          mapped.push({ key: 'qrCode', label: 'ക്യുആർ കോഡ് (QR Code)' });
        }
        setAvailableFields(mapped);
    } else {
        setAvailableFields([
          { key: 'serialNo', label: 'ക്രമനമ്പർ / ID No' },
          { key: 'name', label: 'പേര് / Name' },
          { key: 'phone', label: 'ഫോൺ നമ്പർ / Phone' },
          { key: 'whatsapp', label: 'വാട്സ്ആപ്പ് / WhatsApp' },
          { key: 'categoryName', label: 'വിഭാഗം / Role' },
          { key: 'housePhoto', label: 'പ്രൊഫൈൽ ഫോട്ടോ / Photo' },
          { key: 'barcode', label: 'ബാർകോഡ് (Barcode)' },
          { key: 'qrCode', label: 'ക്യുആർ കോഡ് (QR Code)' }
        ]);
    }
  }, [currentType, questions]);

  useEffect(() => {
    const loadTemplate = async () => {
      setLoading(true);
      try {
        const savedTemplate = await getTemplateByType(currentType);
        if (savedTemplate) {
          setTemplateImg(savedTemplate.backgroundImage);
          setFields(savedTemplate.fields || []);
          setPhotoConfig(savedTemplate.photoConfig);
        } else {
          setTemplateImg(null);
          setFields([]);
          setPhotoConfig(undefined);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [currentType]);

  useEffect(() => {
    if (templateImg && canvasRef.current) {
      let previewData: any = {};
      
      if (currentType !== 'student') {
        previewData = {
          serialNo: currentType === 'faculty' ? 'FAC-0001' : 'COOR-0001',
          name: currentType === 'faculty' ? 'Zainudheen Alavi (ഫാക്കൽറ്റി)' : 'Abdul Rahman (കോർഡിനേറ്റർ)',
          headName: currentType === 'faculty' ? 'Zainudheen Alavi' : 'Abdul Rahman',
          phone: currentType === 'faculty' ? '9876543210' : '9012345678',
          whatsapp: currentType === 'faculty' ? '9876543210' : '9012345678',
          categoryName: currentType === 'faculty' ? 'Faculty (ഫാക്കൽറ്റി)' : 'Coordinator (കോർഡിനേറ്റർ)',
          id: currentType === 'faculty' ? 'FAC-0001' : 'COOR-0001',
          role: currentType
        };
      } else if (surveys.length > 0) {
          const selectedSurvey = surveys[selectedSurveyIndex];
          // Aggregate all parent/household (non-array) sections
          const householdData = Object.keys(selectedSurvey).reduce<Record<string, any>>((acc, key) => {
              if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) return acc;
              const val = selectedSurvey[key];
              if (Array.isArray(val)) return acc; // Skip repeaters
              if (typeof val === 'object' && val !== null) {
                  return { ...acc, ...val };
              }
              return { ...acc, [key]: val };
          }, { id: 'primary-student' });
          
          // Explicitly enforce ID to be 'primary-student'
          householdData.id = 'primary-student';
          
          // USE SECTION A DATA AS THE PRIMARY STUDENT SOURCE (per user request)
          const member = householdData;
          
          // Find age dynamically using the robust helper
          const age = getMemberAge(member, questions);
          const name = member.studentName || getMemberName(member, questions);
          const phone = getMemberPhone(member, questions);
          const photo = getMemberPhoto(member, questions);

          const category = getCategoryForAge(age);
          const categoryName = getCategoryName(category);

          previewData = {
            ...householdData,
            name,
            headName: name,
            phone,
            housePhoto: photo,
            serialNo: formatSerialNoForDisplay(selectedSurvey.sectionA?.serialNo || selectedSurvey.serialNo),
            id: formatSerialNoForDisplay(selectedSurvey.sectionA?.serialNo || selectedSurvey.serialNo),
            categoryName
          };
      } else {
          const listToUse = availableFields.length > 0 ? availableFields : AVAILABLE_FIELDS;
          previewData = {
            ...listToUse.reduce((acc, f) => ({...acc, [f.key]: `[${f.label}]`}), {}),
            id: '[Template]'
          };
      }
      
      drawCardOnCanvas(canvasRef.current, { backgroundImage: templateImg, fields, photoConfig }, { ...previewData, isEditor: true }).then(() => {
        // Draw selection indicator
        if (selectedFieldIndex !== null && fields[selectedFieldIndex] && canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          const field = fields[selectedFieldIndex];
          if (ctx) {
            ctx.strokeStyle = '#3b82f6';
            ctx.lineWidth = 2;
            ctx.setLineDash([2, 2]);
            // Approximate width
            const textWidth = ctx.measureText(String(previewData[field.key] || field.label)).width + 20;
            const drawX = field.alignment === 'center' ? field.x - textWidth/2 : field.alignment === 'right' ? field.x - textWidth : field.x;
            ctx.strokeRect(drawX - 5, field.y - field.fontSize, textWidth, field.fontSize + 10);
            ctx.setLineDash([]);
          }
        }
        
        // Draw photo box indicator if editing it
        if (photoConfig && canvasRef.current && selectedFieldIndex === -2) {
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) {
            ctx.strokeStyle = '#3b82f6';
            ctx.lineWidth = 3;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(photoConfig.x, photoConfig.y, photoConfig.width, photoConfig.height);
            ctx.setLineDash([]);
          }
        }
      });
    }
  }, [templateImg, fields, photoConfig, selectedSurveyIndex, surveys, selectedFieldIndex, availableFields]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Please select an image under 5MB.");
        return;
      }
      setTemplateFile(file);
      
      // Immediate preview
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (evt.target?.result) setTemplateImg(evt.target.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const findFieldAt = (canvasX: number, canvasY: number): number => {
    // 1. Check photo box if it exists
    if (photoConfig) {
      const minX = photoConfig.x;
      const maxX = photoConfig.x + photoConfig.width;
      const minY = photoConfig.y;
      const maxY = photoConfig.y + photoConfig.height;

      if (canvasX >= minX && canvasX <= maxX && canvasY >= minY && canvasY <= maxY) {
        return -2; // Special index for photoConfig
      }
    }

    // 2. Loop through fields backwards so the top-most rendered field gets selected first
    for (let i = fields.length - 1; i >= 0; i--) {
      const f = fields[i];
      let width = 150;
      let height = f.fontSize;
      let offsetTop = f.fontSize;
      let offsetBottom = 5;
      
      if (f.key === 'barcode') {
        width = f.fontSize > 30 ? f.fontSize * 2 : 240;
        height = width * 0.4;
        offsetTop = height;
        offsetBottom = 0;
      } else if (f.key === 'qrCode') {
        width = f.fontSize > 30 ? f.fontSize : 120;
        height = width;
        offsetTop = 0;
        offsetBottom = height;
      } else if (f.key === 'housePhoto') {
        width = f.fontSize > 30 ? f.fontSize : 150;
        height = width * 1.25;
        offsetTop = 0;
        offsetBottom = height;
      } else {
        // Approximate width by character count
        width = Math.max(120, f.label.length * f.fontSize * 0.65);
        height = f.fontSize;
        offsetTop = f.fontSize;
        offsetBottom = 5;
      }

      const minX = f.x - 10;
      const maxX = f.x + width + 10;
      const minY = f.y - offsetTop - 10;
      const maxY = f.y + offsetBottom + 10;

      if (canvasX >= minX && canvasX <= maxX && canvasY >= minY && canvasY <= maxY) {
        return i;
      }
    }
    return -1;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    const idx = findFieldAt(canvasX, canvasY);
    if (idx !== -1) {
      setSelectedFieldIndex(idx);
      setDragState({
        isDragging: true,
        fieldIndex: idx,
        startX: canvasX,
        startY: canvasY,
        originalX: idx === -2 ? (photoConfig?.x || 0) : fields[idx].x,
        originalY: idx === -2 ? (photoConfig?.y || 0) : fields[idx].y,
      });
    } else {
      // If we clicked an empty spot and we have a selected field, we can move that field there
      if (selectedFieldIndex !== null) {
         updateField(selectedFieldIndex, 'x', Math.round(canvasX));
         updateField(selectedFieldIndex, 'y', Math.round(canvasY));
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    if (dragState && dragState.isDragging && dragState.fieldIndex !== null) {
      canvasRef.current.style.cursor = 'grabbing';
      const dx = canvasX - dragState.startX;
      const dy = canvasY - dragState.startY;
      
      const newX = Math.round(dragState.originalX + dx);
      const newY = Math.round(dragState.originalY + dy);
      
      if (dragState.fieldIndex === -2) {
        setPhotoConfig(prev => prev ? { ...prev, x: newX, y: newY } : prev);
      } else {
        setFields(prev => {
          const updated = [...prev];
          if (updated[dragState.fieldIndex!]) {
            updated[dragState.fieldIndex!] = {
              ...updated[dragState.fieldIndex!],
              x: newX,
              y: newY,
            };
          }
          return updated;
        });
      }
      return;
    }

    const idx = findFieldAt(canvasX, canvasY);
    if (idx !== -1) {
      canvasRef.current.style.cursor = 'grab';
    } else {
      canvasRef.current.style.cursor = selectedFieldIndex !== null ? 'crosshair' : 'default';
    }
  };

  const handleMouseUp = () => {
    setDragState(null);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || e.touches.length === 0) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const touch = e.touches[0];
    const canvasX = (touch.clientX - rect.left) * scaleX;
    const canvasY = (touch.clientY - rect.top) * scaleY;

    const idx = findFieldAt(canvasX, canvasY);
    if (idx !== -1) {
      e.preventDefault();
      setSelectedFieldIndex(idx);
      setDragState({
        isDragging: true,
        fieldIndex: idx,
        startX: canvasX,
        startY: canvasY,
        originalX: idx === -2 ? (photoConfig?.x || 0) : fields[idx].x,
        originalY: idx === -2 ? (photoConfig?.y || 0) : fields[idx].y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || e.touches.length === 0 || !dragState || !dragState.isDragging || dragState.fieldIndex === null) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const touch = e.touches[0];
    const canvasX = (touch.clientX - rect.left) * scaleX;
    const canvasY = (touch.clientY - rect.top) * scaleY;

    e.preventDefault();
    const dx = canvasX - dragState.startX;
    const dy = canvasY - dragState.startY;
    
    const newX = Math.round(dragState.originalX + dx);
    const newY = Math.round(dragState.originalY + dy);
    
    if (dragState.fieldIndex === -2) {
      setPhotoConfig(prev => prev ? { ...prev, x: newX, y: newY } : prev);
    } else {
      setFields(prev => {
        const updated = [...prev];
        if (updated[dragState.fieldIndex!]) {
          updated[dragState.fieldIndex!] = {
            ...updated[dragState.fieldIndex!],
            x: newX,
            y: newY,
          };
        }
        return updated;
      });
    }
  };

  const handleTouchEnd = () => {
    setDragState(null);
  };

  const addField = () => {
    const listToUse = availableFields.length > 0 ? availableFields : AVAILABLE_FIELDS;
    const defaultField = listToUse[0] || { key: 'headName', label: 'കുടുംബ നാഥൻ / നാഥ' };
    const newField = { 
      key: defaultField.key, 
      label: defaultField.label, 
      x: 150, 
      y: 150, 
      fontSize: 24, 
      color: '#000000',
      alignment: 'left' as const,
      fontFamily: 'Manjari'
    };
    setFields([...fields, newField]);
    setSelectedFieldIndex(fields.length);
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
    if (selectedFieldIndex === index) setSelectedFieldIndex(null);
  };

  const updateField = (index: number, key: keyof CardField, value: any) => {
    const newFields = [...fields];
    const field = newFields[index];
    
    if (key === 'key') {
       const listToUse = availableFields.length > 0 ? availableFields : AVAILABLE_FIELDS;
       const selected = listToUse.find(f => f.key === value);
       if (selected) {
         field.key = selected.key;
         field.label = selected.label;
       }
    } else {
       // @ts-ignore
       field[key] = value;
    }
    setFields(newFields);
  };

  const updatePhotoConfig = (key: keyof NonNullable<CardTemplate['photoConfig']>, value: number) => {
    setPhotoConfig(prev => {
      const current = prev || { x: 50, y: 50, width: 150, height: 180 };
      return { ...current, [key]: value };
    });
  };

  const saveConfig = async () => {
    if (!templateImg) return;
    setSaving(true);
    try {
      let finalImageUrl = templateImg;
      
      // If new file, compress and prepare Base64 string
      if (templateFile) {
         finalImageUrl = await compressImage(templateFile);
         
         // Safety check for size
         if (finalImageUrl.length > 900000) { // Approx 900KB
             throw new Error("Image is too complex to save. Please try a simpler or smaller background image.");
         }
         
         setTemplateImg(finalImageUrl);
         setTemplateFile(null);
      }

      // Save to Firestore (Base64 string + fields config)
      await saveTemplateByType(currentType, { backgroundImage: finalImageUrl, fields, photoConfig });
      alert('Template configuration saved successfully! You can now generate cards.');
    } catch (error: any) {
      console.error(error);
      alert(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center"><Loader className="animate-spin inline mr-2"/> Loading...</div>;

  return (
    <div className="flex flex-col lg:flex-row gap-6 pb-24 lg:pb-10 relative">
      {/* Settings Panel */}
      <div className="bg-white p-6 rounded-lg shadow-md space-y-6 lg:w-1/3 order-2 lg:order-1 h-fit">
        <h2 className="text-xl font-bold border-b pb-2 text-gray-800">Summer Camp ID Card Settings</h2>

        <div className="space-y-1">
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 text-[11px]">Select Template to Edit</label>
          <select 
            className="w-full border-2 border-blue-100 rounded-lg p-2.5 bg-blue-50 text-blue-950 font-bold focus:ring-2 focus:ring-blue-400 outline-none text-sm"
            value={currentType} 
            onChange={e => setCurrentType(e.target.value as any)}
          >
            <option value="student">Student Card (സ്റ്റുഡന്റ്)</option>
            <option value="faculty">Faculty Card (ഫാക്കൽറ്റി)</option>
            <option value="coordinator">Coordinator Card (കോർഡിനേറ്റർ)</option>
          </select>
        </div>
        
        <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:bg-blue-100 transition-colors relative">
          {templateFile && (
            <div className="absolute top-2 right-2 bg-green-100 text-green-700 px-2 py-1 rounded text-xs flex items-center shadow-sm">
              <Check size={12} className="mr-1"/> New File Selected
            </div>
          )}
          <label className="cursor-pointer block w-full h-full">
            <Upload className="mx-auto text-blue-500 mb-2" size={32} />
            <span className="text-blue-700 font-semibold block">Upload Template Image</span>
            <span className="text-xs text-gray-500 block mt-1">(JPG/PNG. Will be auto-optimized)</span>
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center border-b pb-2">
             <h3 className="font-semibold text-gray-700 flex items-center"><Sliders size={16} className="mr-2"/> Fields Configuration</h3>
             <button onClick={addField} className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded flex items-center hover:bg-emerald-700 transition"><Plus size={14} className="mr-1"/> Add Field</button>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-bold text-gray-700 flex items-center">
                <Check size={14} className={`mr-2 ${photoConfig ? 'text-blue-500' : 'text-gray-300'}`}/> 
                Photo Box Settings
              </h4>
              <button 
                type="button"
                onClick={() => setPhotoConfig(photoConfig ? undefined : { x: 50, y: 50, width: 150, height: 180 })}
                className={`text-[10px] px-2 py-1 rounded font-bold uppercase transition ${photoConfig ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}
              >
                {photoConfig ? 'Remove Box' : 'Enable Box'}
              </button>
            </div>

            {photoConfig && (
              <div 
                className={`space-y-4 cursor-pointer p-3 rounded-xl transition-all border ${selectedFieldIndex === -2 ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-100' : 'bg-white border-gray-100 hover:border-gray-200'}`}
                onClick={() => setSelectedFieldIndex(-2)}
              >
                <div className="flex items-center gap-2 mb-1">
                   <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                      <Camera size={16} />
                   </div>
                   <div>
                      <h5 className="text-xs font-bold text-gray-800">Profile Photo Box</h5>
                      <p className="text-[10px] text-gray-400">Position and size the member photo</p>
                   </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-black text-gray-400 tracking-wider">X Position</label>
                    <div className="relative">
                      <input type="number" className="bg-gray-50 border-0 rounded-lg w-full p-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500" value={photoConfig.x} onChange={e => updatePhotoConfig('x', parseInt(e.target.value))} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-black text-gray-400 tracking-wider">Y Position</label>
                    <div className="relative">
                      <input type="number" className="bg-gray-50 border-0 rounded-lg w-full p-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500" value={photoConfig.y} onChange={e => updatePhotoConfig('y', parseInt(e.target.value))} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-black text-gray-400 tracking-wider">Width (px)</label>
                    <div className="relative">
                      <input type="number" className="bg-gray-50 border-0 rounded-lg w-full p-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500" value={photoConfig.width} onChange={e => updatePhotoConfig('width', parseInt(e.target.value))} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-black text-gray-400 tracking-wider">Height (px)</label>
                    <div className="relative">
                      <input type="number" className="bg-gray-50 border-0 rounded-lg w-full p-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500" value={photoConfig.height} onChange={e => updatePhotoConfig('height', parseInt(e.target.value))} />
                    </div>
                  </div>
                </div>
                
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                   <p className="text-[10px] text-emerald-700 leading-relaxed">
                     <Check size={10} className="inline mr-1"/> 
                     Drag the green dashed box on the card to position it. The member's uploaded photo will automatically <b>center-crop</b> to fit these dimensions.
                   </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {fields.map((field, idx) => (
            <div 
              key={idx} 
              className={`p-3 rounded text-sm relative border cursor-pointer transition-all ${selectedFieldIndex === idx ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
              onClick={() => setSelectedFieldIndex(idx)}
            >
              <button onClick={(e) => { e.stopPropagation(); removeField(idx); }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={16}/></button>
              
              <div className="flex items-center mb-2 text-xs text-gray-500 font-bold uppercase tracking-wider">
                {selectedFieldIndex === idx && <MousePointer2 size={12} className="mr-1 text-blue-600 animate-pulse" />}
                Field {idx + 1}: <span className="ml-1 text-blue-800">{(availableFields.length > 0 ? availableFields : AVAILABLE_FIELDS).find(f => f.key === field.key)?.label || field.key}</span>
              </div>

              <div className="mb-3 pr-6">
                 <select 
                    className="w-full border border-gray-300 rounded p-1.5 bg-white text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
                    value={field.key}
                    onChange={(e) => updateField(idx, 'key', e.target.value)}
                 >
                    {(availableFields.length > 0 ? availableFields : AVAILABLE_FIELDS).map(f => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                 </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">X Position</label>
                   <input type="number" className="border border-gray-300 rounded w-full p-1" value={field.x} onChange={e => updateField(idx, 'x', parseInt(e.target.value))} />
                </div>
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">Y Position</label>
                   <input type="number" className="border border-gray-300 rounded w-full p-1" value={field.y} onChange={e => updateField(idx, 'y', parseInt(e.target.value))} />
                </div>
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">Size (px)</label>
                   <input type="number" className="border border-gray-300 rounded w-full p-1" value={field.fontSize} onChange={e => updateField(idx, 'fontSize', parseInt(e.target.value))} />
                </div>
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">Color</label>
                   <input type="color" className="border border-gray-300 rounded w-full h-7 p-0 cursor-pointer" value={field.color} onChange={e => updateField(idx, 'color', e.target.value)} />
                </div>
                <div className="col-span-2">
                   <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Text Alignment</label>
                   <div className="flex bg-white border border-gray-300 rounded overflow-hidden">
                     {['left', 'center', 'right'].map((align) => (
                       <button
                         key={align}
                         type="button"
                         onClick={() => updateField(idx, 'alignment', align)}
                         className={`flex-1 py-1 text-[10px] uppercase font-bold transition-colors ${
                           (field.alignment || 'left') === align 
                             ? 'bg-blue-600 text-white' 
                             : 'text-gray-600 hover:bg-gray-100'
                         }`}
                       >
                         {align}
                       </button>
                     ))}
                   </div>
                </div>
              </div>

              <div className="mt-2">
                 <label className="text-[10px] uppercase font-bold text-gray-500 block mb-0.5">Font Family</label>
                 <select 
                    className="w-full border border-gray-300 rounded p-1.5 bg-white text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none text-xs"
                    value={field.fontFamily || 'Arial'}
                    onChange={(e) => updateField(idx, 'fontFamily', e.target.value)}
                 >
                    {AVAILABLE_FONTS.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                 </select>
              </div>
            </div>
          ))}
          {fields.length === 0 && <p className="text-gray-400 text-center text-sm py-4 italic">No fields added yet.</p>}
          </div>
          
          <button 
            onClick={saveConfig} 
            disabled={saving}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-bold hover:bg-emerald-700 shadow-sm transition-transform active:scale-95 flex justify-center items-center"
          >
             {saving ? <Loader className="animate-spin mr-2" size={20}/> : <Save className="mr-2" size={20}/>}
             {saving ? 'Compressing & Saving...' : 'Save Template Settings'}
          </button>
        </div>
      </div>

      {/* Preview Panel - Sticky on Desktop */}
      <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center lg:w-2/3 order-1 lg:order-2 h-fit overflow-hidden lg:sticky lg:top-4">
        <div className="w-full flex justify-between items-center mb-4 border-b pb-2">
            <h2 className="text-xl font-bold text-gray-800">Preview & Edit</h2>
            <div className="flex items-center space-x-2">
               <AlertCircle size={14} className="text-blue-500"/>
               <span className="text-xs text-gray-500">Changes are saved only when you click "Save Template Settings"</span>
            </div>
        </div>
        
        {currentType === 'student' && surveys.length > 0 && (
          <div className="w-full mb-4 bg-gray-50 p-3 rounded border border-gray-200 space-y-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Select Household / Survey:</label>
              <select className="w-full border border-gray-300 p-2 rounded bg-white text-sm focus:ring-2 focus:ring-blue-400 outline-none" value={selectedSurveyIndex} onChange={e => setSelectedSurveyIndex(parseInt(e.target.value))}>
                {surveys.map((s, i) => (
                  <option key={s.id} value={i}>
                    {getMemberName(Object.keys(s).reduce((acc, k) => (Array.isArray(s[k]) ? acc : typeof s[k] === 'object' && s[k] !== null ? { ...acc, ...s[k] } : { ...acc, [k]: s[k] }), {}), questions) || 'No Student Name'} 
                    (Serial: {formatSerialNoForDisplay(s.sectionA?.serialNo || s.serialNo)})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="relative border-2 border-gray-200 border-dashed shadow-sm rounded-lg mb-6 w-full bg-gray-50 overflow-auto min-h-[300px] flex items-center justify-center p-4">
             {!templateImg && (
                <div className="text-center p-10">
                    <p className="text-gray-400 italic mb-2">No template uploaded.</p>
                    <p className="text-xs text-gray-400">Please upload an image from the settings panel.</p>
                </div>
             )}
             <div className="inline-block relative">
                <canvas 
                  ref={canvasRef} 
                  className={`max-w-none h-auto block shadow-lg ${!templateImg ? 'hidden' : ''}`}
                  style={{ maxWidth: '100%', userSelect: 'none', touchAction: 'none' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
             </div>
        </div>
      </div>
    </div>
  );
};

export default CardGenerator;